import "zod/compile";
import { z } from "zod";
import { planningLevelStepKindSchema } from "./procurementPlanningLevels";
import { forecastRunMethodConfigSchema } from "./procurementForecastRuns";

/**
 * Org-wide forecast settings (`im_forecast_settings`, GEN-2699): the engine
 * parameters every forecast run of an organization uses. Every setting is
 * optional. An unset one runs on the engine's code default
 * (forecasting/runContext.ts), so an org that never saved anything forecasts
 * exactly as before. Shared so the contract, the service and the General tab
 * validate against one set of bounds.
 */

/** The forecaster's demand shapes, the keys of `availabilityByClass`. */
export const FORECAST_DEMAND_CLASSES = [
  "smooth",
  "erratic",
  "intermittent",
  "lumpy",
] as const;
export type ForecastDemandClass = (typeof FORECAST_DEMAND_CLASSES)[number];

/** The settings, in the order the General tab and Run history show them. */
export const FORECAST_SETTING_KEYS = [
  "historyMonths",
  "horizonMonths",
  "availabilityByClass",
  "availabilityByPopularity",
  "leadTimeDefaultDays",
  "maxUnobservedLeadTimeShare",
  "planningLevelRules",
] as const;
export type ForecastSettingKey = (typeof FORECAST_SETTING_KEYS)[number];

/**
 * Inclusive bounds. History and horizon match the nightly workflow step's own
 * limits (run-demand-forecast.step.ts): fewer than 12 months cannot fit a
 * seasonal cycle, and past 24 forecast months the curve is a flat guess.
 */
export const FORECAST_SETTINGS_BOUNDS = {
  historyMonths: { min: 12, max: 60 },
  horizonMonths: { min: 1, max: 24 },
  leadTimeDefaultDays: { min: 1, max: 365 },
} as const;

/** Popularity codes are short AS400 values, matched case-insensitively. */
export const FORECAST_POPULARITY_CODE_MAX_LENGTH = 10;

/**
 * Shares are kept to four decimals, a percent to two: the precision the
 * General tab edits, so a stored share opens and re-saves unchanged.
 */
export const FORECAST_SHARE_DECIMALS = 4;

export function hasForecastSharePrecision(share: number): boolean {
  return Number(share.toFixed(FORECAST_SHARE_DECIMALS)) === share;
}

const sharePrecision = {
  message: `Use at most ${FORECAST_SHARE_DECIMALS} decimals.`,
};

/**
 * A target availability is a probability: 0 and 1 are excluded because the
 * safety-stock z-score is infinite at both.
 */
export const forecastTargetAvailabilitySchema = z
  .number()
  .gt(0)
  .lt(1)
  .refine(hasForecastSharePrecision, sharePrecision);

const availabilityByClassSchema = z.partialRecord(
  z.enum(FORECAST_DEMAND_CLASSES),
  forecastTargetAvailabilitySchema,
);

/** How the engine matches a code: the AS400 column is space-padded. */
export function normalizeForecastPopularityCode(code: string): string {
  return code.trim().toUpperCase();
}

const availabilityByPopularitySchema = z
  .record(
    z.string().trim().min(1).max(FORECAST_POPULARITY_CODE_MAX_LENGTH),
    forecastTargetAvailabilitySchema,
  )
  .refine(
    (targets) =>
      Object.keys(targets).every(
        (code) =>
          normalizeForecastPopularityCode(code).length <=
          FORECAST_POPULARITY_CODE_MAX_LENGTH,
      ),
    {
      message: `Popularity codes must be at most ${FORECAST_POPULARITY_CODE_MAX_LENGTH} characters once uppercased.`,
    },
  )
  .refine(
    (targets) => {
      const codes = Object.keys(targets).map(normalizeForecastPopularityCode);
      return new Set(codes).size === codes.length;
    },
    { message: "Popularity codes must be unique, ignoring case." },
  );

/**
 * The stored settings. Null means "not set: use the engine default". A class
 * left out of `availabilityByClass` likewise keeps its default.
 */
export const planningLevelRulesSchema = z.object({
  order: z
    .array(planningLevelStepKindSchema)
    .max(3)
    .refine((order) => new Set(order).size === order.length, {
      message: "Planning level steps must be unique.",
    }),
});

export const forecastSettingsSchema = z.object({
  planningLevelRules: planningLevelRulesSchema.nullable().default(null),
  historyMonths: z
    .number()
    .int()
    .min(FORECAST_SETTINGS_BOUNDS.historyMonths.min)
    .max(FORECAST_SETTINGS_BOUNDS.historyMonths.max)
    .nullable(),
  horizonMonths: z
    .number()
    .int()
    .min(FORECAST_SETTINGS_BOUNDS.horizonMonths.min)
    .max(FORECAST_SETTINGS_BOUNDS.horizonMonths.max)
    .nullable(),
  availabilityByClass: availabilityByClassSchema.nullable(),
  availabilityByPopularity: availabilityByPopularitySchema.nullable(),
  leadTimeDefaultDays: z
    .number()
    .int()
    .min(FORECAST_SETTINGS_BOUNDS.leadTimeDefaultDays.min)
    .max(FORECAST_SETTINGS_BOUNDS.leadTimeDefaultDays.max)
    .nullable(),
  /** Share of a division's series allowed to be unobserved; 1 disables the guard. */
  maxUnobservedLeadTimeShare: z
    .number()
    .gt(0)
    .max(1)
    .refine(hasForecastSharePrecision, sharePrecision)
    .nullable(),
});
export type ForecastSettings = z.infer<typeof forecastSettingsSchema>;

/** Every setting unset: what an org that never saved the General tab has. */
export const EMPTY_FORECAST_SETTINGS: ForecastSettings = {
  planningLevelRules: null,
  historyMonths: null,
  horizonMonths: null,
  availabilityByClass: null,
  availabilityByPopularity: null,
  leadTimeDefaultDays: null,
  maxUnobservedLeadTimeShare: null,
};

/** The values a run resolves to once every unset setting takes its default. */
export const forecastSettingsValuesSchema =
  forecastRunMethodConfigSchema.extend({
    historyMonths: z.number().int(),
    horizonMonths: z.number().int(),
  });
export type ForecastSettingsValues = z.infer<
  typeof forecastSettingsValuesSchema
>;
